/* eslint-disable @typescript-eslint/no-extra-semi */
import React, { Suspense, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { MemoryRouter as Router } from 'react-router-dom';
import './styles/globals.css';
import './styles/animate.css';
import 'react-toastify/dist/ReactToastify.css';
import App from './App';
import PackageOrderForm from './pages/PackageOrder/PackageOrderForm';
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import HttpApi from 'i18next-http-backend';
import Loading from './components/Loading';
import logger from './utils/monitor-service';
import ApplicationSettingsProvider from './providers/ApplicationSettingsProvider';
import en from './i18n/en.json';
import sv from './i18n/sv.json';
import dayjs from 'dayjs';
import updateLocale from 'dayjs/plugin/updateLocale';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import 'dayjs/locale/en';
import './i18n/dayjs.sv';

const currentLocale = WISORY_SDK_SETTINGS.LOCALE || 'sv';
dayjs.extend(updateLocale);
dayjs.extend(localizedFormat);
dayjs.updateLocale(currentLocale, {
  weekStart: 1,
});
dayjs.locale(currentLocale);

i18n
  .use(HttpApi)
  .use(initReactI18next) // passes i18n down to react-i18next
  .init({
    fallbackLng: 'en',
    lng: WISORY_SDK_SETTINGS.LOCALE,
    backend: {
      loadPath: WISORY_SDK_SETTINGS.TRANSLATIONS_URL,
      parse(data) {
        const translations = JSON.parse(data) as Array<{
          ACF: { key: string; value: string };
        }>;

        const localResources = WISORY_SDK_SETTINGS.LOCALE === 'sv' ? sv : en;

        const translation = {
          ...localResources,
          ...translations.filter(item => item.ACF.value).reduce((result, item) => ({ ...result, [item.ACF.key]: item.ACF.value }), {}),
        };

        return translation;
      },
    },
  });

const CenteredLoadingBlock = () => (
  <div className="flex items-center justify-center" style={{ minHeight: 300 }}>
    <Loading />
  </div>
);

const BookAdvisorFormWrapper = ({ id, profileName }: { id: number; profileName: string }) => {
  useEffect(() => {
    //@ts-ignore Hide the generated div from Wordpress
    const wordpressBlock = document.querySelector<HTMLDivElement>('.wisory-sdk-block + div');

    // Hide wordpress geenrated block that take space.
    if (wordpressBlock) {
      wordpressBlock.style.display = 'none';
    }

    logger.init();
  }, []);

  return (
    <Suspense fallback={<CenteredLoadingBlock />}>
      <ApplicationSettingsProvider>
        <Router initialEntries={[`/profile/${id}?profileName=${profileName}`]} initialIndex={0}>
          <App />
        </Router>
      </ApplicationSettingsProvider>
    </Suspense>
  );
};

const PackageOrderFormWrapper = ({ packageId }: { packageId: string }) => {
  useEffect(() => {
    logger.init();
  }, []);

  return (
    <Suspense fallback={<CenteredLoadingBlock />}>
      <ApplicationSettingsProvider>
        <PackageOrderForm packageId={packageId} />
      </ApplicationSettingsProvider>
    </Suspense>
  );
};

const WisorySDK = {
  bookAdvisor({ id, profileName }: { id: number; profileName: string }, elementID: string) {
    ReactDOM.render(
      <BookAdvisorFormWrapper id={id} profileName={profileName} />,
      typeof elementID == 'string' ? document.getElementById(elementID) : elementID
    );
  },
  packageOrderForm({ packageId }: { packageId: string }, elementID: string) {
    ReactDOM.render(
      <PackageOrderFormWrapper packageId={packageId} />,
      typeof elementID == 'string' ? document.getElementById(elementID) : elementID
    );
  },
};

const renderContentIfTheSdkNotWorking = () => {
  const element = document.querySelector('.wisory-sdk-block div[data-widget-id]');
  const script = element?.querySelector('script');

  if (element && script) {
    const scriptContent = script.innerText;

    logger.logInfo('RE_RENDER_BOOKING_FORM');

    try {
      // eslint-disable-next-line no-eval
      eval(scriptContent);
    } catch (e) {
      console.log('Cannot execute the bookAdvisor function', e);
    }
  }
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
(window as any).WisorySDK = WisorySDK;

// Wordpress need to load sdk scripts first before execute the "bookAdvisor" global function to render the content on dialog
// In case of the execution script "bookAdvisor" run before the sdk is loaded, we need to re-trigger it
renderContentIfTheSdkNotWorking();
